<template>
  <div class="card"> 
    <div class="card__media">
      <div class="card__promoted" v-if="restaurant.promoted && restaurant.promoted?.promotionTypes?.includes('offers')">
        {{ restaurant.promoted?.text }}
      </div>
      <div class="absolute z-10 bottom-0 left-0 w-full p-3 md:p-4" v-if="restaurant.logo">
        <div class="relative z-10 w-16 h-8 sm:w-24 sm:h-12 invert" v-if="restaurant.logo">
          <AtomImage class="h-full !object-contain object-left-bottom" :src="useCloudinaryImage(restaurant.logo, 300, null, 'fit,e_trim')" :alt="restaurant.name" />
        </div>
        <div class="absolute bottom-0 left-0 w-full h-full bg-gradient-to-t from-25% from-black/80"></div>
      </div>
      <AtomImage class="!object-cover" :src="imageUrl" :alt="restaurant.name" />
    </div>
    <div class="card__info">
      <div class="grid gap-1">
        <h3 class="card__heading">{{ title }}</h3>
        <div class="relative z-10 flex items-center gap-2 my-1" v-if="featuredTags.length">
          <div class="text-sm whitespace-nowrap text-ocean border border-ocean/10 bg-sky rounded px-2 py-0.5" v-for="tag in featuredTags" :key="tag.id">
            {{ tag.label }}
          </div>
        </div>
        <p class="card__description">{{ restaurant.shortDescription }} {{ description }}</p> 
      </div>
      <div class="grid gap-4 mt-4 md:mt-6">
        <AtomRestaurantLocation
          :name="restaurant.address"
          :address="restaurant.city" />
        <div class="flex gap-4 text-sm">
          <button class="button-coral" @click="useOpenWidget(restaurant.hash, meal.id, fat.amount, date)" v-if="restaurant?.bookable">
            {{ $t('shared.bookTable') }}
          </button>
          <NuxtLink v-if="restaurant?.slug" class="button-coral-outline" :to="{ name: 'restaurants-slug', params: { slug: restaurant.slug }}" :aria-label="restaurant.name">
            {{ $t('shared.readMore') }}
          </NuxtLink>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const { t } = useI18n()
const fat = useFatStore()

const props = defineProps({
  restaurant: {
    type: Object,
    default: () => {}
  },
  meal: {
    type: Object,
    default: () => {}
  },
  position: {
    type: Number,
    default: 0
  },
  date: {
    type: String,
    default: ''
  },
})

// Content based on alt type
const title = computed(() => props.meal.altType === 3
  ? t(`guide.offers.tableToday.title`, { name: props.restaurant.name })
  : (props.meal.altType === 2
    ? t(`guide.offers.earlyBird.title`, { name: props.restaurant.name })
    : t(`guide.offers.standard.title`, { name: props.restaurant.name })))

const description = computed(() => props.meal.altType === 3
  ? t(`guide.offers.tableToday.description.${(props.position-1)%6}`)
  : (props.meal.altType === 2
    ? t(`guide.offers.earlyBird.description.${(props.position-1)%6}`)
    : t(`guide.offers.standard.description.${(props.position-1)%6}`)))

// Featured tags
const featuredTags = computed(() => useFeaturedTags(props.restaurant.tags, 3))

// Get custom image or restaurant cover
const imageUrl = props.meal?.image || useCloudinaryImage(props.restaurant.cover, 600, 600)
</script>

<style lang="scss" scoped>
.card {
  @apply grid md:flex md:flex-row-reverse md:items-center;
  @apply bg-mist rounded overflow-hidden;

  &__media {
    @apply relative aspect-video md:aspect-auto;
    @apply md:w-2/5 md:h-full;
  }

  &__info {
    @apply grid gap-1 p-4 md:py-6 md:px-8;
    @apply md:w-3/5;
  }

  &__heading {
    @apply font-heading font-medium; 
    @apply text-2xl md:text-4xl;
  }

  &__description {
    @apply mt-1 leading-6;
  }
}
</style>
